import React, { useEffect, useState } from 'react';
import { Drawer, Descriptions, Card, Table, Button, message, Space, Upload, Modal, Empty, Select, Spin, Avatar, Popconfirm, Image, DatePicker, Switch, Tag } from 'antd';
import { UploadOutlined, DownloadOutlined, DeleteOutlined, CameraOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { documentService } from '../services/documents';
import { documentTypeService } from '../services/documentTypes';
import { profilePhotoService } from '../services/profilePhoto';
import StatusDropdown from './StatusDropdown';
import ExpirationDate from './ExpirationDate';
import { useAuth } from '../contexts/AuthContext';

const { Option } = Select;

function ItemDetailDrawer({ open, onClose, item, itemType }) {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [allDocTypes, setAllDocTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadLoadingId, setDownloadLoadingId] = useState(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [profilePhotoLoading, setProfilePhotoLoading] = useState(false);
  const [profilePhotoUploadModalOpen, setProfilePhotoUploadModalOpen] = useState(false);
  const [profilePhotoFileList, setProfilePhotoFileList] = useState([]);
  const [selectedOptionalDocType, setSelectedOptionalDocType] = useState(null);
  const [savingExpirationId, setSavingExpirationId] = useState(null);
  const [updatingNotificationId, setUpdatingNotificationId] = useState(null);
  const canManageDocumentCatalog = user?.role === 'admin' || user?.role === 'qhse_nke';
  const isDocumentReadOnly = user?.role === 'all_external';

  useEffect(() => {
    if (open && item) {
      // Reset foto profilo quando si cambia item
      if (profilePhotoUrl && profilePhotoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(profilePhotoUrl);
      }
      setProfilePhotoUrl(null);
      loadDocuments();
      loadAllDocTypes();
      if (itemType === 'worker') {
        loadProfilePhoto();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item, itemType]);

  // Cleanup blob URL quando il componente si smonta o quando si cambia item
  useEffect(() => {
    return () => {
      if (profilePhotoUrl && profilePhotoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(profilePhotoUrl);
      }
    };
  }, [profilePhotoUrl]);

  const normalizeType = (value) => (value || '').toString().trim().toLowerCase();

  const buildItemTypeKeys = (rawType) => {
    const t = normalizeType(rawType);
    if (t === 'company') return ['company', 'companies', 'tenant', 'tenants', 'azienda'];
    if (t === 'worker') return ['worker', 'workers', 'lavoratore'];
    if (t === 'vehicle') return ['vehicle', 'vehicles', 'veicolo'];
    if (t === 'equipment') return ['equipment', 'equipments', 'attrezzatura'];
    if (t === 'chemical') return ['chemical', 'chemicals', 'prodotto chimico'];
    return [t];
  };

  const matchesItemType = (docType, currentType) => {
    if (!docType?.applicable_item_types) return false;
    const targetKeys = buildItemTypeKeys(currentType);
    const docKeys = docType.applicable_item_types.map(normalizeType);
    return docKeys.some((k) => targetKeys.includes(k));
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      let docs = await documentService.list({ item_id: item.id });
      docs = docs || [];

      if (!canManageDocumentCatalog) {
        setDocuments(docs);
        return;
      }

      // Carica i tipi documento per creare automaticamente quelli obbligatori mancanti
      const allTypes = await documentTypeService.list();

      const applicableTypes = allTypes.filter((dt) => matchesItemType(dt, itemType));

      // Identifica i documenti obbligatori mancanti e creali automaticamente
      const mandatoryTypes = applicableTypes.filter((dt) => {
        const mode = dt.mandatory_mode || (dt.is_mandatory_by_default ? 'all' : 'none');
        if (mode === 'all') return true;
        if (mode === 'specific') return (dt.mandatory_for_tenants || []).includes(item?.tenant_id);
        return false;
      });

      const existingDocTypes = new Set(docs.map((d) => normalizeType(d.document_type)));
      const missingMandatoryTypes = mandatoryTypes.filter((dt) => !existingDocTypes.has(normalizeType(dt.name)));

      // Crea i documenti obbligatori mancanti
      for (const docType of missingMandatoryTypes) {
        try {
          const newDoc = await documentService.create({
            item_id: item.id,
            document_type: docType.name,
            is_mandatory: true,
            status: 'missing',
          });
          docs.push(newDoc);
        } catch (err) {
          console.error(`Errore creazione documento ${docType.name}:`, err);
        }
      }

      setDocuments(docs);
    } catch (e) {
      message.error('Errore caricamento documenti');
    } finally {
      setLoading(false);
    }
  };

  const loadAllDocTypes = async () => {
    if (!canManageDocumentCatalog) {
      setAllDocTypes([]);
      return;
    }

    try {
      const allTypes = await documentTypeService.list();
      setAllDocTypes(allTypes || []);
    } catch (e) {
      console.error('Errore caricamento tipi documento:', e);
    }
  };

  const loadProfilePhoto = async () => {
    if (itemType !== 'worker') return;
    setProfilePhotoUrl(null);
    try {
      // Prova prima il download diretto (più affidabile)
      const data = await profilePhotoService.getProfilePhotoBlob(item.id);
      setProfilePhotoUrl(data.url);
    } catch (e) {
      // Se il download diretto fallisce, prova l'URL presigned come fallback
      try {
        const data = await profilePhotoService.getProfilePhotoUrl(item.id);
        setProfilePhotoUrl(data.url);
      } catch (err) {
        // Se anche il fallback fallisce, manteniamo il blob URL locale se esiste
        setProfilePhotoUrl(null);
      }
    }
  };

  const handleUploadFile = (docType) => {
    if (isDocumentReadOnly) return;
    setSelectedDocType(docType);
    setFileList([]);
    setUploadModalOpen(true);
  };

  const handlePreview = async (record) => {
    if (record.files && record.files.length > 0) {
      setPreviewLoading(true);
      setPreviewUrl(documentService.getPreviewUrl(record.files[0].id));
      setPreviewModalOpen(true);
    } else {
      message.warning('Nessun file disponibile per l\'anteprima');
    }
  };

  const handleDownload = async (record) => {
    if (record.files && record.files.length > 0) {
      try {
        setDownloadLoadingId(record.id);
        const file = record.files[0];
        const blob = await documentService.downloadFile(file.id);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (e) {
        message.error('Errore nel download del file');
      } finally {
        setDownloadLoadingId(null);
      }
    } else {
      message.warning('Nessun file disponibile per il download');
    }
  };

  const handleUploadSubmit = async () => {
    if (isDocumentReadOnly) return;
    if (fileList.length === 0) {
      message.warning('Seleziona un file PDF');
      return;
    }

    try {
      const file = fileList[0].originFileObj || fileList[0];
      
      // Crea il documento se non esiste già
      let docId = documents.find(d => d.document_type === selectedDocType.name)?.id;
      
      if (!docId) {
        // Crea un nuovo documento
        const newDoc = await documentService.create({
          item_id: item.id,
          document_type: selectedDocType.name,
          is_mandatory: selectedDocType.is_mandatory_by_default,
          status: 'missing',
        });
        docId = newDoc.id;
      }
      
      await documentService.uploadFile(docId, file);
      message.success('File caricato');
      setUploadModalOpen(false);
      setFileList([]);
      loadDocuments();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
  };

  const uploadProps = {
    accept: '.pdf',
    maxCount: 1,
    listType: 'text',
    beforeUpload: (file) => {
      const isPdf = (file.type === 'application/pdf') || (/\.pdf$/i.test(file.name || ''));
      if (!isPdf) {
        message.error('Solo file PDF sono consentiti');
        return Upload.LIST_IGNORE;
      }
      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        message.error(`File troppo grande: ${(file.size / (1024 * 1024)).toFixed(2)}MB. Dimensione massima: 5MB`);
        return Upload.LIST_IGNORE;
      }
      setFileList([{ originFileObj: file, name: file.name }]);
      return false;
    },
    fileList,
    onRemove: () => setFileList([]),
  };

  const handleStatusChange = async (docId, newStatus, record) => {
    if (isDocumentReadOnly) return;
    try {
      // Se il documento non esiste ancora (record virtuale), lo creiamo
      if (!docId) {
        await documentService.create({
          item_id: item.id,
          document_type: record.document_type,
          status: newStatus,
          is_mandatory: record.is_mandatory
        });
        message.success('Stato aggiornato');
      } else {
        // Se esiste, lo aggiorniamo
        await documentService.update(docId, { status: newStatus });
        message.success('Stato aggiornato');
      }
      loadDocuments();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
  };

  const handleExpirationChange = async (record, date) => {
    if (isDocumentReadOnly) return;
    if (!record?.id) return;
    const expirationDate = date ? date.format('YYYY-MM-DD') : null;
    try {
      setSavingExpirationId(record.id);
      await documentService.update(record.id, { expiration_date: expirationDate });
      message.success({ content: 'Scadenza aggiornata', key: 'expiration-updated' });
      loadDocuments();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setSavingExpirationId(null);
    }
  };

  const handleUploadProfilePhoto = async () => {
    if (isDocumentReadOnly) return;
    if (profilePhotoFileList.length === 0) {
      message.warning('Seleziona un\'immagine');
      return;
    }

    try {
      setProfilePhotoLoading(true);
      const file = profilePhotoFileList[0].originFileObj || profilePhotoFileList[0];
      
      // Crea URL locale per anteprima immediata
      const localUrl = URL.createObjectURL(file);
      setProfilePhotoUrl(localUrl);
      
      // Upload al server
      await profilePhotoService.uploadProfilePhoto(item.id, file);
      message.success('Foto profilo caricata');
      setProfilePhotoUploadModalOpen(false);
      setProfilePhotoFileList([]);
      // Manteniamo il blob URL locale - non ricarichiamo dal server
    } catch (e) {
      // Se l'upload fallisce, pulisci l'URL locale
      setProfilePhotoUrl(null);
      const detail = e?.response?.data?.detail || 'Errore';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setProfilePhotoLoading(false);
    }
  };

  const handleDeleteProfilePhoto = async () => {
    if (isDocumentReadOnly) return;
    try {
      setProfilePhotoLoading(true);
      await profilePhotoService.deleteProfilePhoto(item.id);
      message.success('Foto profilo eliminata');
      setProfilePhotoUrl(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setProfilePhotoLoading(false);
    }
  };

  const profilePhotoUploadProps = {
    accept: 'image/*',
    maxCount: 1,
    listType: 'text',
    beforeUpload: (file) => {
      if (!file.type.startsWith('image/')) {
        message.error('Solo file immagine');
        return Upload.LIST_IGNORE;
      }
      setProfilePhotoFileList([{ originFileObj: file, name: file.name }]);
      return false;
    },
    fileList: profilePhotoFileList,
    onRemove: () => setProfilePhotoFileList([]),
  };

  const handleAddOptionalDoc = async () => {
    if (isDocumentReadOnly) return;
    if (!selectedOptionalDocType) {
      message.warning('Seleziona un documento facoltativo');
      return;
    }

    try {
      // Crea il documento facoltativo per questo item
      await documentService.create({
        item_id: item.id,
        document_type: selectedOptionalDocType,
        is_mandatory: false,
        status: 'missing',
      });
      message.success('Documento facoltativo aggiunto');
      setSelectedOptionalDocType(null);
      loadDocuments();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
  };

  const handleDeleteOptionalDoc = async (record) => {
    if (isDocumentReadOnly) return;
    try {
      console.log('Deleting document:', record.id);
      await documentService.delete(record.id);
      message.success('Documento facoltativo rimosso');
      loadDocuments();
    } catch (e) {
      console.error('Delete error:', e);
      const detail = e?.response?.data?.detail || 'Errore';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
  };

  // Filtra documenti obbligatori vs facoltativi
  const isMandatory = (docTypeName, docRecord) => {
    if (!canManageDocumentCatalog || allDocTypes.length === 0) {
      return docRecord?.is_mandatory || false;
    }

    const normalizedName = normalizeType(docTypeName);
    const type = allDocTypes.find((dt) => normalizeType(dt.name) === normalizedName);
    // Valida che il documento sia applicabile a questo item type
    if (!type || !matchesItemType(type, itemType)) return docRecord?.is_mandatory || false;
    const mode = type.mandatory_mode || (type.is_mandatory_by_default ? 'all' : 'none');
    if (mode === 'all') return true;
    if (mode === 'specific') return (type.mandatory_for_tenants || []).includes(item?.tenant_id);
    return false;
  };

  const handleNotificationToggle = async (record, newValue) => {
    if (isDocumentReadOnly) return;
    try {
      setUpdatingNotificationId(record.id);
      await documentService.updateNotification(record.id, newValue);
      message.success('Notifica aggiornata');
      loadDocuments();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setUpdatingNotificationId(null);
    }
  };

  const mandatoryDocs = documents.filter((d) => isMandatory(d.document_type, d));
  const optionalDocs = documents.filter((d) => !isMandatory(d.document_type, d));

  // Documenti facoltativi disponibili (non già in questo item)
  const availableOptionalTypes = allDocTypes.filter((dt) =>
    !isDocumentReadOnly &&
    matchesItemType(dt, itemType) &&
    (dt.mandatory_mode || 'none') === 'none' &&
    !documents.some((d) => d.document_type === dt.name)
  );

  const getStatusTag = (status) => {
    const statusMap = {
      missing: { color: 'red', label: 'MANCANTE' },
      da_controllare: { color: 'orange', label: 'DA CONTROLLARE' },
      idoneo: { color: 'green', label: 'IDONEO' },
      non_idoneo: { color: 'red', label: 'NON IDONEO' },
      non_applicabile: { color: 'default', label: 'NON APPLICABILE' },
    };
    const config = statusMap[status] || { color: 'default', label: (status || '-').toUpperCase() };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const docColumns = [
    { title: 'Tipo Documento', dataIndex: 'document_type', key: 'document_type', width: 260 },
    {
      title: 'Scadenza',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      width: 220,
      render: (v, record) => (
        isDocumentReadOnly ? (
          <ExpirationDate date={v} />
        ) : (
          <DatePicker
            value={v ? dayjs(v) : null}
            format="DD/MM/YYYY"
            allowClear
            size="small"
            onChange={(date) => handleExpirationChange(record, date)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={savingExpirationId === record.id}
            style={{ width: '100%' }}
          />
        )
      ),
    },
    { 
      title: 'Stato', 
      dataIndex: 'status', 
      key: 'status', 
      width: 200,
      render: (status, record) => {
        const hasFiles = record.files && record.files.length > 0;
        if (isDocumentReadOnly) {
          return getStatusTag(status);
        }
        return (
          <StatusDropdown
            status={status}
            onChange={(newStatus) => handleStatusChange(record.id, newStatus, record)}
            hasFiles={hasFiles}
            record={record}
          />
        );
      }
    },
    {
      title: 'Notifiche',
      key: 'notifications',
      width: 120,
      render: (_, record) => {
        const hasFiles = record.files && record.files.length > 0;
        if (isDocumentReadOnly) {
          return record.notify_on_expiration !== false ? 'Sì' : 'No';
        }
        return (
          <Switch
            checked={record.notify_on_expiration !== false}
            onChange={(checked) => handleNotificationToggle(record, checked)}
            loading={updatingNotificationId === record.id}
            disabled={!hasFiles}
            size="small"
            title={!hasFiles ? "Aggiungi un file per abilitare le notifiche" : ""}
          />
        );
      },
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 200,
      render: (_, record) => {
        const hasFiles = record.files && record.files.length > 0;
        if (isDocumentReadOnly) {
          return hasFiles ? (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(record);
              }}
              loading={downloadLoadingId === record.id}
            >
              Scarica
            </Button>
          ) : '-';
        }
        return (
          <Space>
            <Button 
              size="small" 
              icon={<UploadOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                handleUploadFile({ name: record.document_type, is_mandatory_by_default: true });
              }}
              disabled={record.status === 'non_applicabile' || (downloadLoadingId !== null && downloadLoadingId === record.id)}
            >
              Carica
            </Button>
            {hasFiles && (
              <Button 
                size="small" 
                icon={<DownloadOutlined />} 
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(record);
                }}
                loading={downloadLoadingId === record.id}
              >
                Scarica
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  const optionalDocColumns = [
    { title: 'Tipo Documento', dataIndex: 'document_type', key: 'document_type', width: 260 },
    {
      title: 'Scadenza',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      width: 220,
      render: (v, record) => (
        isDocumentReadOnly ? (
          <ExpirationDate date={v} />
        ) : (
          <DatePicker
            value={v ? dayjs(v) : null}
            format="DD/MM/YYYY"
            allowClear
            size="small"
            onChange={(date) => handleExpirationChange(record, date)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={savingExpirationId === record.id}
            style={{ width: '100%' }}
          />
        )
      ),
    },
    { 
      title: 'Stato', 
      dataIndex: 'status', 
      key: 'status', 
      width: 200,
      render: (status, record) => {
        const hasFiles = record.files && record.files.length > 0;
        if (isDocumentReadOnly) {
          return getStatusTag(status);
        }
        return (
          <StatusDropdown
            status={status}
            onChange={(newStatus) => handleStatusChange(record.id, newStatus, record)}
            hasFiles={hasFiles}
            record={record}
          />
        );
      }
    },
    {
      title: 'Notifiche',
      key: 'notifications',
      width: 120,
      render: (_, record) => {
        const hasFiles = record.files && record.files.length > 0;
        if (isDocumentReadOnly) {
          return record.notify_on_expiration !== false ? 'Sì' : 'No';
        }
        return (
          <Switch
            checked={record.notify_on_expiration !== false}
            onChange={(checked) => handleNotificationToggle(record, checked)}
            loading={updatingNotificationId === record.id}
            disabled={!hasFiles}
            size="small"
            title={!hasFiles ? "Aggiungi un file per abilitare le notifiche" : ""}
          />
        );
      },
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 250,
      render: (_, record) => {
        const hasFiles = record.files && record.files.length > 0;
        if (isDocumentReadOnly) {
          return hasFiles ? (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(record);
              }}
              loading={downloadLoadingId === record.id}
            >
              Scarica
            </Button>
          ) : '-';
        }
        const canDelete = !hasFiles && (record.status === 'missing' || record.status === 'non_applicabile');
        return (
          <Space>
            <Button 
              size="small" 
              icon={<UploadOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                handleUploadFile({ name: record.document_type, is_mandatory_by_default: true });
              }}
              disabled={record.status === 'non_applicabile' || (downloadLoadingId !== null && downloadLoadingId === record.id)}
            >
              Carica
            </Button>
            {hasFiles && (
              <Button 
                size="small" 
                icon={<DownloadOutlined />} 
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(record);
                }}
                loading={downloadLoadingId === record.id}
              >
                Scarica
              </Button>
            )}
            {canDelete && (
              <Popconfirm
                title="Rimuovi documento facoltativo"
                description="Sei sicuro di voler rimuovere questo documento?"
                onConfirm={() => handleDeleteOptionalDoc(record)}
                okText="Sì"
                cancelText="No"
              >
                <Button 
                  size="small" 
                  danger 
                  icon={<DeleteOutlined />}
                >
                  Rimuovi
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  const renderItemProperties = () => {
    if (!item) return null;

    const commonItems = [
      { key: 'id', label: 'ID', children: item.id },
      { key: 'is_active', label: 'Attivo', children: item.is_active ? 'Sì' : 'No' },
      { key: 'created_at', label: 'Creato il', children: item.created_at ? dayjs(item.created_at).format('DD/MM/YYYY HH:mm') : '-' },
    ];

    let specificItems = [];

    switch (itemType) {
      case 'vehicle':
        specificItems = [
          { key: 'identifier', label: 'Identificativo', children: item.standard_fields?.identifier },
        ];
        break;
      case 'equipment':
        specificItems = [
          { key: 'serial_number', label: 'Numero di serie', children: item.standard_fields?.serial_number },
        ];
        break;
      case 'chemical':
        specificItems = [
          { key: 'name', label: 'Nome prodotto', children: item.standard_fields?.name },
        ];
        break;
      case 'worker':
        specificItems = [
          { key: 'first_name', label: 'Nome', children: item.standard_fields?.first_name },
          { key: 'last_name', label: 'Cognome', children: item.standard_fields?.last_name },
          { key: 'fiscal_code', label: 'Codice Fiscale', children: item.standard_fields?.fiscal_code },
          { key: 'job_role', label: 'Mansione', children: item.standard_fields?.job_role },
          { key: 'hire_date', label: 'Data Assunzione', children: item.standard_fields?.hire_date ? dayjs(item.standard_fields.hire_date).format('DD/MM/YYYY') : '-' },
          { key: 'birth_date', label: 'Data Nascita', children: item.standard_fields?.birth_date ? dayjs(item.standard_fields.birth_date).format('DD/MM/YYYY') : '-' },
          { key: 'birth_place', label: 'Luogo Nascita', children: item.standard_fields?.birth_place },
        ];
        break;
      case 'company':
        specificItems = [
          { key: 'name', label: 'Ragione Sociale', children: item.name },
          { key: 'code', label: 'Codice', children: item.code },
          { key: 'fiscal_code', label: 'Codice Fiscale', children: item.fiscal_code },
          { key: 'vat_number', label: 'Partita IVA', children: item.vat_number },
          { key: 'contact_person', label: 'Referente', children: item.contact_person },
          { key: 'email', label: 'Email', children: item.email },
          { key: 'pec_email', label: 'PEC', children: item.pec_email },
          { key: 'phone', label: 'Telefono', children: item.phone },
          { key: 'address', label: 'Indirizzo', children: item.address },
          { key: 'rls', label: 'RLS', children: item.rls },
          { key: 'medico_competente', label: 'Medico competente', children: item.medico_competente },
          { key: 'rspp', label: 'RSPP', children: item.rspp },
        ];
        break;
      default:
        break;
    }

    return <Descriptions bordered column={2} items={[...specificItems, ...commonItems]} />;
  };

  const getTitle = () => {
    if (!item) return '';
    switch (itemType) {
      case 'vehicle':
        return `Veicolo: ${item.standard_fields?.identifier || item.id}`;
      case 'equipment':
        return `Attrezzatura: ${item.standard_fields?.serial_number || item.id}`;
      case 'chemical':
        return `Prodotto Chimico: ${item.standard_fields?.name || item.id}`;
      case 'worker':
        return `Lavoratore: ${item.standard_fields?.first_name || ''} ${item.standard_fields?.last_name || ''}`.trim() || `ID ${item.id}`;
      case 'company':
        return `Azienda: ${item.name || item.id}`;
      default:
        return `Item #${item.id}`;
    }
  };

  return (
    <>
      <Drawer
        title={getTitle()}
        placement="right"
        onClose={onClose}
        open={open}
        width="50%"
      >
        {itemType === 'worker' && (
          <Card title="Foto Profilo" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }} align="center">
              {profilePhotoUrl ? (
                <>
                  <Image
                    src={profilePhotoUrl}
                    width={140}
                    height={180}
                    style={{ objectFit: 'cover', border: '2px solid #d9d9d9', borderRadius: '4px' }}
                    preview={{ mask: 'Visualizza' }}
                  />
                  {!isDocumentReadOnly && (
                    <Space>
                      <Button
                        type="primary"
                        icon={<CameraOutlined />}
                        onClick={() => setProfilePhotoUploadModalOpen(true)}
                        loading={profilePhotoLoading}
                        size="small"
                      >
                        Sostituisci
                      </Button>
                      <Popconfirm
                        title="Elimina foto profilo"
                        description="Sei sicuro di voler eliminare la foto profilo?"
                        onConfirm={handleDeleteProfilePhoto}
                        okText="Sì"
                        cancelText="No"
                      >
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          size="small"
                          loading={profilePhotoLoading}
                        >
                          Elimina
                        </Button>
                      </Popconfirm>
                    </Space>
                  )}
                </>
              ) : (
                <>
                  <Avatar size={140} icon={<CameraOutlined />} />
                  {!isDocumentReadOnly && (
                    <Button
                      type="primary"
                      icon={<UploadOutlined />}
                      onClick={() => setProfilePhotoUploadModalOpen(true)}
                      loading={profilePhotoLoading}
                      size="small"
                    >
                      Carica Foto Profilo
                    </Button>
                  )}
                </>
              )}
            </Space>
          </Card>
        )}

        <Card title="Proprietà" style={{ marginBottom: 16 }}>
          {renderItemProperties()}
        </Card>

        <Card title="Documenti Obbligatori">
          {mandatoryDocs.length > 0 ? (
            <Table
              loading={loading}
              dataSource={mandatoryDocs}
              columns={docColumns}
              rowKey={(record) => record.id}
              pagination={false}
              locale={{ emptyText: <Empty description="Nessun documento" /> }}
              onRow={(record) => ({
                onClick: () => {
                  if (record.files && record.files.length > 0) {
                    handlePreview(record);
                  }
                },
                style: { cursor: record.files && record.files.length > 0 ? 'pointer' : 'default' }
              })}
            />
          ) : (
            <Empty description="Nessun documento obbligatorio" />
          )}
        </Card>

        {(optionalDocs.length > 0 || availableOptionalTypes.length > 0) && (
          <Card title="Documenti Facoltativi" style={{ marginTop: 16 }}>
            {optionalDocs.length > 0 ? (
              <Table
                loading={loading}
                dataSource={optionalDocs}
                columns={optionalDocColumns}
                rowKey={(record) => record.id}
                pagination={false}
                locale={{ emptyText: <Empty description="Nessun documento facoltativo aggiunto" /> }}
                onRow={(record) => ({
                  onClick: () => {
                    if (record.files && record.files.length > 0) {
                      handlePreview(record);
                    }
                  },
                  style: { cursor: record.files && record.files.length > 0 ? 'pointer' : 'default' }
                })}
              />
            ) : (
              <Empty description="Nessun documento facoltativo aggiunto" />
            )}
            
            {availableOptionalTypes.length > 0 && (
              <Space style={{ marginTop: 16, width: '100%' }}>
                <Select
                  style={{ flex: 1 }}
                  placeholder="Seleziona un documento facoltativo..."
                  value={selectedOptionalDocType}
                  onChange={setSelectedOptionalDocType}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {availableOptionalTypes.map(dt => (
                    <Option key={dt.name} value={dt.name}>
                      {dt.name}
                    </Option>
                  ))}
                </Select>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={handleAddOptionalDoc}
                >
                  Aggiungi
                </Button>
              </Space>
            )}
          </Card>
        )}
      </Drawer>

      <Modal
        title="Anteprima Documento"
        open={previewModalOpen}
        onCancel={() => {
          setPreviewModalOpen(false);
          setPreviewUrl(null);
          setPreviewLoading(false);
        }}
        footer={previewUrl ? (
          <Button onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}>
            Apri in nuova scheda
          </Button>
        ) : null}
        width={900}
        style={{ top: 20 }}
      >
        {previewLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
            <Spin size="large" tip="Caricamento documento in corso..." />
          </div>
        )}
        {previewUrl && (
          <iframe
            title="Anteprima documento"
            src={previewUrl}
            onLoad={() => setPreviewLoading(false)}
            style={{
              width: '100%',
              height: '80vh',
              border: 'none',
              display: previewLoading ? 'none' : 'block',
            }}
          />
        )}
      </Modal>

      <Modal
        title={`Carica file - ${selectedDocType?.name}`}
        open={uploadModalOpen}
        onCancel={() => { setUploadModalOpen(false); setFileList([]); }}
        onOk={handleUploadSubmit}
        okText="Carica"
        cancelText="Annulla"
      >
        <Upload.Dragger {...uploadProps} maxCount={1}>
          <p style={{ fontSize: 16 }}>
            <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          </p>
          <p style={{ fontSize: 14, fontWeight: 500 }}>Clicca o trascina un file PDF qui</p>
          <p style={{ fontSize: 12, color: '#666' }}>Solo file PDF - Max 5MB (compresso automaticamente)</p>
        </Upload.Dragger>
      </Modal>

      <Modal
        title="Carica Foto Profilo"
        open={profilePhotoUploadModalOpen}
        onCancel={() => { setProfilePhotoUploadModalOpen(false); setProfilePhotoFileList([]); }}
        onOk={handleUploadProfilePhoto}
        okText="Carica"
        cancelText="Annulla"
        confirmLoading={profilePhotoLoading}
      >
        <Upload.Dragger {...profilePhotoUploadProps} maxCount={1}>
          <p style={{ fontSize: 16 }}>
            <CameraOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          </p>
          <p style={{ fontSize: 14, fontWeight: 500 }}>Clicca o trascina un'immagine qui</p>
          <p style={{ fontSize: 12, color: '#666' }}>JPG, PNG, WebP - Max 5MB</p>
        </Upload.Dragger>
      </Modal>
    </>
  );
}

export default ItemDetailDrawer;
